import { findScrollContainer } from '@apia/util';
import { TDistinctors } from '@apia/dom-store';

export type TRowDistinctions = {
  rowIndex: number;
};

export type TListboxState = {
  /**
   * El múltipleSearch está de acuerdo a la especificación de wai-aria. Está
   * habilitado por defecto e implica que si el usuario presiona una "rápida
   * sucesión" (según palabras de wai-aria) de caracteres, se irá
   * alternando el foco entre los elementos que comiencen con la cadena
   * introducida.
   */
  allowMultipleCharacterSearch?: boolean;
  allowSelection: boolean;
  /**
   * El singleCharacterSearch está de acuerdo a la especificación de wai-aria.
   * Está habilitado por defecto e implica que si el usuario presiona una
   * "lenta sucesión" (según palabras de wai-aria) de caracteres, se irá
   * alternando el foco entre los elementos que comiencen con la letra
   * introducida.
   */
  allowSingleCharacterSearch?: boolean;
  focusedRow: number;
  hiddenRows: number[];
  id: string;
  isEverythingSelected: boolean;
  isMultipleSelection: boolean;
  lastSelectedRow: number;
  rowsCount: number;
  /**
   * La función de búsqueda debe tomar el label de un campo y el texto
   * introducido en el campo de búsqueda y determinar si el mismo debe ser
   * mostrado o no. Por defecto, se usa una función de matcheo estricto.
   */
  searchFunction: (label: string, searchString: string) => boolean;
  selectedRows: number[];
  shownRows: number[];
  showSearchbox: boolean;
  /**
   * Este método permite determinar si un elemento comienza con una cadena de
   * texto en particular. Por defecto se usa String.startsWith, sin embargo se
   * permite el pasaje de este método para situaciones particulares, ejemplo
   * si se deseara incluir elementos sin tomar en cuenta los espacios.
   */
  startsWithFunction: (label: string, searchString: string) => boolean;
  typing: string | null;
};

export function makeRowKey(rowIndex: TRowDistinctions | number) {
  return `row_${typeof rowIndex === 'number' ? rowIndex : rowIndex.rowIndex}`;
}

let uniqueidIndex = 0;
export function uniqueId(prefix?: string) {
  return `${prefix ?? ''}${uniqueidIndex++}`;
}

function getDistinctors(storeId: string): TDistinctors<TListboxState> {
  return {
    listbox: [
      (_: void, state) => {
        return {
          etc: {
            'aria-activedescendant':
              state.shownRows.length > 0
                ? `${storeId}__row__${state.focusedRow}`
                : undefined,
          },
        };
      },
    ],
    row: [
      (distinctions: TRowDistinctions, state) => {
        return {
          classList: {
            add: [
              'listbox__row',
              state.focusedRow === distinctions.rowIndex ? 'focused' : '',
              state.allowSelection &&
              state.selectedRows.includes(distinctions.rowIndex)
                ? 'selected'
                : '',
            ],
            remove: [
              state.focusedRow === distinctions.rowIndex ? '' : 'focused',
              state.allowSelection &&
              state.selectedRows.includes(distinctions.rowIndex)
                ? ''
                : 'selected',
            ],
          },
          dataset: {
            add: {
              focused: state.focusedRow === distinctions.rowIndex,
            },
          },
          etc: {
            ...(state.allowSelection
              ? {
                  'aria-selected': state.selectedRows.includes(
                    distinctions.rowIndex,
                  ),
                }
              : null),
            id: `${storeId}__row__${distinctions.rowIndex}`,
          },
        };
      },
      (prevState: TListboxState, nextState: TListboxState) => {
        const element = document.querySelector<HTMLElement>(
          `#${storeId} .listbox__row[data-focused="true"]`,
        );

        if (element) {
          if (prevState.focusedRow > nextState.focusedRow) {
            const scrollContainer = findScrollContainer(element);

            if (scrollContainer && scrollContainer !== document.body) {
              scrollContainer.scrollIntoView({
                behavior: 'auto',
                block: 'nearest',
                inline: 'nearest',
              });
              const elRect = element.getBoundingClientRect();
              const contRect = scrollContainer.getBoundingClientRect();

              if (elRect.top - contRect.top < elRect.height)
                scrollContainer.scrollTop -= elRect.height;
            }
          }
          element.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      },
    ],
    searchbox: [
      (_: void, state) => {
        return {
          classList: { add: ['listbox__searchbox'] },
          etc: {
            'aria-activedescendant':
              state.shownRows.length > 0
                ? `${storeId}__row__${state.focusedRow}`
                : undefined,
          },
        };
      },
    ],
  };
}
const defaultState: Omit<
  TListboxState,
  'id' | 'showSearchbox' | 'shownRows' | 'searchFunction' | 'startsWithFunction'
> = {
  allowSelection: true,
  focusedRow: 0,
  hiddenRows: [],
  isEverythingSelected: false,
  isMultipleSelection: false,
  lastSelectedRow: -1,
  rowsCount: 0,
  selectedRows: [],
  typing: null,
};

const keysMakers = {
  row: makeRowKey,
  listbox: () => 'listbox',
  searchbox: () => 'searchbox',
};

function getInitialState(
  initialState: Partial<TListboxState> &
    Pick<TListboxState, 'id' | 'showSearchbox'>,
): TListboxState {
  return {
    ...defaultState,
    ...initialState,

    searchFunction:
      initialState.searchFunction ??
      ((label, searchString) => !!label.match(new RegExp(searchString))),

    startsWithFunction:
      initialState.startsWithFunction ??
      ((label, searchString) => {
        return label.startsWith(searchString);
      }),

    shownRows: new Array(initialState.rowsCount ?? 0).fill(1).map((_, i) => i),
  };
}

export { getDistinctors, getInitialState, keysMakers };
